import { DateTime } from 'luxon';
import { calculateTrend, MonthStatus, TrendType } from '../utils/reports';
/** Compression levels for data series */
export var CompressionLevel;
(function (CompressionLevel) {
    CompressionLevel["MONTHLY"] = "MONTHLY";
    CompressionLevel["QUARTERLY"] = "QUARTERLY";
    CompressionLevel["YEARLY"] = "YEARLY";
})(CompressionLevel || (CompressionLevel = {}));
/** Quarter names for display */
export const QUARTERS = ['Q1', 'Q2', 'Q3', 'Q4'];
/**
 * Determines the appropriate compression level based on the number of months in the date range
 * @param monthCount - Number of months in the range
 * @returns Appropriate compression level
 */
export function determineCompressionLevel(monthCount) {
    if (monthCount <= 12) {
        return CompressionLevel.MONTHLY;
    }
    else if (monthCount <= 48) {
        // Up to 4 years -> quarters (max 16 quarters)
        return CompressionLevel.QUARTERLY;
    }
    else {
        return CompressionLevel.YEARLY;
    }
}
/**
 * Calculates the number of months between two dates
 * @param dateRange - Date range specification
 * @returns Number of months in the range
 */
export function calculateMonthCount(dateRange) {
    const { fromMonth, fromYear, toMonth, toYear } = dateRange;
    if (fromYear === toYear) {
        return toMonth - fromMonth + 1;
    }
    let count = 0;
    // First year (partial)
    count += 12 - fromMonth + 1;
    // Full years in between
    count += (toYear - fromYear - 1) * 12;
    // Last year (partial)
    count += toMonth;
    return count;
}
/**
 * Generates month keys in the original format for a date range
 * @param dateRange - Date range specification
 * @returns Array of month keys in original format
 */
export function generateOriginalMonthKeys(dateRange) {
    const { fromMonth, fromYear, toMonth, toYear } = dateRange;
    const monthKeys = [];
    const spansMultipleYears = fromYear !== toYear;
    for (let year = fromYear; year <= toYear; year++) {
        const startMonth = year === fromYear ? fromMonth : 1;
        const endMonth = year === toYear ? toMonth : 12;
        for (let month = startMonth; month <= endMonth; month++) {
            const monthName = DateTime.fromObject({ year, month }).toFormat('MMM');
            const key = spansMultipleYears ? `${monthName} ${year}` : monthName;
            monthKeys.push(key);
        }
    }
    return monthKeys;
}
/**
 * Generates compressed period keys for the given date range and compression level
 * @param dateRange - Date range specification
 * @param compressionLevel - Level of compression to apply
 * @param originalMonthKeys - Original month keys to map from
 * @returns Array of compressed period information
 */
export function generateCompressedPeriods(dateRange, compressionLevel, originalMonthKeys) {
    const { fromMonth, fromYear, toMonth, toYear } = dateRange;
    const periods = [];
    const spansMultipleYears = fromYear !== toYear;
    switch (compressionLevel) {
        case CompressionLevel.MONTHLY:
            // Return original format wrapped in CompressedPeriod structure
            return originalMonthKeys.map((key) => {
                const parsed = parseOriginalMonthKey(key, dateRange);
                return {
                    key,
                    label: key,
                    year: parsed.year,
                    startMonth: parsed.month,
                    endMonth: parsed.month,
                    sourceMonthKeys: [key],
                };
            });
        case CompressionLevel.QUARTERLY:
            // Generate quarterly periods
            for (let year = fromYear; year <= toYear; year++) {
                const startQuarter = year === fromYear ? Math.ceil(fromMonth / 3) : 1;
                const endQuarter = year === toYear ? Math.ceil(toMonth / 3) : 4;
                for (let quarter = startQuarter; quarter <= endQuarter; quarter++) {
                    const quarterName = QUARTERS[quarter - 1];
                    const key = spansMultipleYears ? `${quarterName} ${year}` : quarterName;
                    const label = key;
                    // Calculate the actual month range for this quarter
                    let quarterStartMonth = (quarter - 1) * 3 + 1;
                    let quarterEndMonth = quarter * 3;
                    // Adjust for partial quarters at the boundaries
                    if (year === fromYear && quarter === startQuarter) {
                        quarterStartMonth = Math.max(quarterStartMonth, fromMonth);
                    }
                    if (year === toYear && quarter === endQuarter) {
                        quarterEndMonth = Math.min(quarterEndMonth, toMonth);
                    }
                    // Find all source month keys that belong to this quarter
                    const sourceMonthKeys = originalMonthKeys.filter((monthKey) => {
                        const parsed = parseOriginalMonthKey(monthKey, dateRange);
                        return parsed.year === year && parsed.month >= quarterStartMonth && parsed.month <= quarterEndMonth;
                    });
                    periods.push({
                        key,
                        label,
                        year,
                        startMonth: quarterStartMonth,
                        endMonth: quarterEndMonth,
                        sourceMonthKeys,
                    });
                }
            }
            break;
        case CompressionLevel.YEARLY:
            // Generate yearly periods
            for (let year = fromYear; year <= toYear; year++) {
                const key = year.toString();
                const label = key;
                // Calculate the actual month range for this year
                let yearStartMonth = year === fromYear ? fromMonth : 1;
                let yearEndMonth = year === toYear ? toMonth : 12;
                // Find all source month keys that belong to this year
                const sourceMonthKeys = originalMonthKeys.filter((monthKey) => {
                    const parsed = parseOriginalMonthKey(monthKey, dateRange);
                    return parsed.year === year;
                });
                periods.push({
                    key,
                    label,
                    year,
                    startMonth: yearStartMonth,
                    endMonth: yearEndMonth,
                    sourceMonthKeys,
                });
            }
            break;
    }
    return periods;
}
/**
 * Parses an original month key to extract year and month information
 * @param monthKey - Month key (e.g., "Jan", "Jan 2024")
 * @param dateRange - Date range for context
 * @returns Year and month numbers
 */
export function parseOriginalMonthKey(monthKey, dateRange) {
    if (monthKey.includes(' ')) {
        const [monthName, yearStr] = monthKey.split(' ');
        const month = DateTime.fromFormat(monthName, 'MMM').month;
        const year = parseInt(yearStr, 10);
        return { year, month };
    }
    else {
        const month = DateTime.fromFormat(monthKey, 'MMM').month;
        return { year: dateRange.fromYear, month };
    }
}
/**
 * Compresses monthly trends data according to compressed periods
 * @param monthlyTrends - Original monthly trends data
 * @param compressedPeriods - Array of compressed period definitions
 * @returns Compressed trends data
 */
export function compressMonthlyTrends(monthlyTrends, compressedPeriods) {
    const compressed = {};
    let previousValue = 0;
    for (let i = 0; i < compressedPeriods.length; i++) {
        const period = compressedPeriods[i];
        let totalValue = 0;
        let hasData = false;
        let periodStatus = MonthStatus.FUTURE;
        // Sum up values for all source months in this period
        for (const sourceMonthKey of period.sourceMonthKeys) {
            const monthData = monthlyTrends[sourceMonthKey];
            if (monthData) {
                totalValue += monthData.value;
                hasData = true;
                // Update period status (use the most "current" status)
                if (monthData.status === MonthStatus.CURRENT) {
                    periodStatus = MonthStatus.CURRENT;
                }
                else if (monthData.status === MonthStatus.PAST && periodStatus === MonthStatus.FUTURE) {
                    periodStatus = MonthStatus.PAST;
                }
            }
        }
        // Only add periods that have data and are not future periods
        if (hasData && periodStatus !== MonthStatus.FUTURE) {
            // Calculate trend
            let trend = TrendType.NONE;
            if (i > 0) {
                trend = calculateTrend(totalValue, previousValue);
            }
            compressed[period.key] = {
                value: totalValue,
                trend,
                status: periodStatus,
            };
            previousValue = totalValue;
        }
    }
    return compressed;
}
